/**
 * Family Activity Widget Container
 *
 * App-layer container that connects the presentational
 * {@link FamilyActivityWidget} to the calendar and todo stores.
 *
 * The activity feed merges calendar events with tasks. `DashboardPage` loads
 * the calendar store, but nothing on the `/dashboard` route loaded the todo
 * store, so the feed silently omitted every task. This container therefore
 * triggers the todo load itself rather than relying on a sibling widget's
 * effect: a feed that quietly drops half its sources is precisely the failure
 * this fixes, and it should not come back if the widget grid is rearranged.
 *
 * @module app/widgets/FamilyActivityWidgetContainer
 */

import { useCalendarStore } from '@/features/family-calendar/stores/calendarStore';
import { useTodoStore } from '@/features/todo-list/stores/todoStore';
import { FamilyActivityWidget } from '@/shared/components/widgets/FamilyActivityWidget';
import { useFamilyScopedLoad } from '@/shared/hooks/useFamilyScopedLoad';

interface FamilyActivityWidgetContainerProps {
  readonly count?: number;
  readonly className?: string;
  readonly showHeader?: boolean;
}

export function FamilyActivityWidgetContainer(
  props: FamilyActivityWidgetContainerProps
) {
  const events = useCalendarStore((state) => state.events);
  const members = useCalendarStore((state) => state.members);
  const tasks = useTodoStore((state) => state.tasks);
  const loadTodos = useTodoStore((state) => state.load);

  // `load()` is idempotent (guarded by `isLoaded` / `isLoading`), so this and
  // the identical call in `TodoProgressWidgetContainer` share one request when
  // both widgets are on screen. The calendar half is already loaded by
  // `DashboardPage`, the only route that mounts this container.
  useFamilyScopedLoad(loadTodos);

  return (
    <FamilyActivityWidget
      {...props}
      events={events}
      members={members}
      tasks={tasks}
    />
  );
}
